import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { processesQueryKey } from '../../queries/sox';
import { fetchSoxCycles, fetchProcessesForCycle } from '../../request/sox';

/**
 * SOX cycles list + per-cycle processes (parallel queries keyed by cycle id + audit year).
 * Pass auditYearId to scope both cycles and processes to a specific year; omit for all years.
 */
export function useSoxDashboardQueries(auditYearId) {
  const cyclesQueryKey = auditYearId ? ['sox-cycles', auditYearId] : ['sox-cycles'];

  const {
    data: soxCyclesFromAPI = [],
    isLoading: loadingCycles
  } = useQuery({
    queryKey: cyclesQueryKey,
    queryFn: () => fetchSoxCycles(auditYearId)
  });

  const processQueriesConfig = useMemo(
    () =>
      soxCyclesFromAPI.map((c) => {
        const cycleListKey = c.cycle_id ?? c.id;
        return {
          queryKey: [...processesQueryKey(cycleListKey), auditYearId ?? ''],
          queryFn: () => fetchProcessesForCycle(cycleListKey, auditYearId)
        };
      }),
    [soxCyclesFromAPI, auditYearId]
  );

  const processQueryResults = useQueries({ queries: processQueriesConfig });

  const processesFromAPI = useMemo(() => {
    const map = {};
    soxCyclesFromAPI.forEach((c, i) => {
      const row = processQueryResults[i];
      const cycleListKey = c.cycle_id ?? c.id;
      map[cycleListKey] = row?.data ?? [];
    });
    return map;
  }, [soxCyclesFromAPI, processQueryResults]);

  return {
    soxCyclesFromAPI,
    loadingCycles,
    processQueryResults,
    processesFromAPI
  };
}
